import api from './api';

export type MerchantProgramType =
  | 'fee_waiver'
  | 'fee_schedule'
  | 'announcement'
  | 'new_merchant_registration';
export type MerchantProgramStatus =
  | 'scheduled'
  | 'active'
  | 'ended'
  | 'cancelled';

export interface RegistrationPackageConfig {
  subscription_package_id?: number;
  name: string;
  description?: string;
  price: number;
  duration?: number;
  features?: string[];
  is_popular?: boolean;
}

export interface NewMerchantRegistrationConfig {
  free_registration_enabled?: boolean;
  packages: RegistrationPackageConfig[];
}

export interface FeeScheduleConfig {
  fee_type: 'percent' | 'fixed';
  fee_rate?: number;
  fee_amount_vnd?: number;
}

export interface MerchantProgramRecord {
  id: number;
  restaurant_id: number | null;
  restaurant_name?: string;
  program_type: MerchantProgramType;
  name: string;
  description?: string | null;
  status: MerchantProgramStatus;
  derived_status?: MerchantProgramStatus;
  starts_at?: string | null;
  ends_at?: string | null;
  config:
    | FeeScheduleConfig
    | NewMerchantRegistrationConfig
    | Record<string, unknown>;
  show_banner: boolean;
  banner_threshold_days: number;
  banner_title?: string | null;
  banner_body?: string | null;
  banner_dismissible: boolean;
  created_by?: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface MerchantProgramListParams {
  page?: number;
  limit?: number;
  restaurantId?: number;
  programType?: MerchantProgramType;
  status?: MerchantProgramStatus;
}

export interface MerchantProgramListResponse {
  success: boolean;
  data: MerchantProgramRecord[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  message?: string;
}

export interface MerchantProgramPayload {
  restaurantId?: number;
  programType: MerchantProgramType;
  name: string;
  description?: string;
  status?: MerchantProgramStatus;
  startsAt?: string;
  endsAt?: string;
  config?:
    | FeeScheduleConfig
    | NewMerchantRegistrationConfig
    | Record<string, unknown>;
  showBanner?: boolean;
  bannerThresholdDays?: number;
  bannerTitle?: string;
  bannerBody?: string;
  bannerDismissible?: boolean;
}

export async function fetchMerchantPrograms(
  params: MerchantProgramListParams = {}
): Promise<MerchantProgramListResponse> {
  const res = await api.get<MerchantProgramListResponse>('/merchant-programs', {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 20,
      restaurantId: params.restaurantId,
      programType: params.programType,
      status: params.status,
      _t: Date.now(),
    },
  });
  return res.data;
}

export async function fetchMerchantProgramById(id: number): Promise<{
  success: boolean;
  data?: MerchantProgramRecord;
  message?: string;
}> {
  const res = await api.get(`/merchant-programs/${id}`);
  return res.data;
}

export async function createMerchantProgram(
  body: MerchantProgramPayload
): Promise<{
  success: boolean;
  data?: MerchantProgramRecord;
  message?: string;
}> {
  const res = await api.post('/merchant-programs', body);
  return res.data;
}

export async function updateMerchantProgram(
  id: number,
  body: Partial<MerchantProgramPayload>
): Promise<{
  success: boolean;
  data?: MerchantProgramRecord;
  message?: string;
}> {
  const res = await api.put(`/merchant-programs/${id}`, body);
  return res.data;
}

export async function deleteMerchantProgram(
  id: number
): Promise<{ success: boolean; message?: string }> {
  const res = await api.delete(`/merchant-programs/${id}`);
  return res.data;
}

export interface MerchantOption {
  restaurantId: number;
  label: string;
}

export async function fetchMerchantOptions(): Promise<MerchantOption[]> {
  const res = await api.get<{
    success: boolean;
    data: Array<{
      id: number;
      name: string;
      restaurantId?: number;
      restaurantName?: string;
    }>;
  }>('/merchants', { params: { page: 1, limit: 500 } });
  if (!res.data.success || !Array.isArray(res.data.data)) return [];
  return res.data.data
    .map(m => {
      const restaurantId = m.restaurantId ?? m.id;
      if (!restaurantId) return null;
      return {
        restaurantId,
        label: m.restaurantName
          ? `${m.restaurantName} (#${restaurantId})`
          : `${m.name} (#${restaurantId})`,
      } as MerchantOption;
    })
    .filter((x): x is MerchantOption => x != null);
}
